/**
 * MealEditSheet — edit or delete a planned meal (Wave 3 follow-up).
 *
 * A BottomSheet form mirroring the meal draft card's fields (title / date /
 * ingredient list / notes). Save emits the edited payload; the parent runs the
 * admin-scope-gated PATCH. When that PATCH is HARD-BLOCKED on a household
 * allergen (server 422), the parent sets `blocked` → this sheet shows a red
 * banner + an explicit override toggle and re-submits with override.
 *
 * Delete is a two-step inline confirm so a stray tap can't wipe a plan.
 *
 * Utseende (#157, steg 7c), etter Hendelse-arket fra steg 7b (D9): et høyt ark
 * med topphodet X · «Endre middag» · Lagre (`SheetHeader`), feltene som
 * `field` med `SectionLabel`, og Slett alene nederst.
 *
 * Allergiblokken er sikkerhetskritisk og står øverst i arket, rett under
 * Lagre, i den røde `bad`-tonen (en blokkering, ikke et råd). Lagre er
 * deaktivert til avkrysningen «Jeg tar ansvar» er satt, som før. Den er
 * fortsatt en vanlig avkrysningsboks i en etikett, ikke en Switch, så
 * bekreftelsen leses og virker som før. Kontrasten (`bad` på `bad/10` over
 * arket) står i contrast.test.ts.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Meal } from '@stell/shared';
import { BottomSheet, Button, Icon, SheetHeader } from '../../components/primitives';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import type { MealPayload } from './useMeals';

interface MealEditSheetProps {
  open: boolean;
  meal: Meal | null;
  /** Set true by the parent when the last save was 422 allergen_blocked. */
  blocked: boolean;
  busy: boolean;
  onSave: (payload: MealPayload, override: boolean) => void;
  onDelete: () => void;
  onClose: () => void;
}

const TITLE_ID = 'meal-edit-sheet-title';

export function MealEditSheet({ open, meal, blocked, busy, onSave, onDelete, onClose }: MealEditSheetProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [ingredients, setIngredients] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [override, setOverride] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Re-seed the form whenever a different meal opens.
  useEffect(() => {
    if (!meal) return;
    setTitle(meal.title);
    setDate(meal.date ?? '');
    setIngredients(meal.ingredients.length > 0 ? meal.ingredients : ['']);
    setNotes(meal.notes ?? '');
    setOverride(false);
    setConfirmDelete(false);
  }, [meal]);

  function save() {
    const clean = ingredients.map((s) => s.trim()).filter(Boolean);
    const payload: MealPayload = {
      title: title.trim(),
      ingredients: clean,
      ...(date ? { date } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    onSave(payload, override);
  }

  const header = (
    <SheetHeader
      titleId={TITLE_ID}
      title={t('meny.edit.title')}
      onClose={onClose}
      close={{ label: t('common.cancel'), disabled: busy, testId: 'meal-edit-cancel' }}
      action={
        <Button
          size={44}
          onClick={save}
          disabled={busy || !title.trim() || (blocked && !override)}
          data-testid="meal-edit-save"
        >
          {t('meny.edit.save')}
        </Button>
      }
    />
  );

  return (
    <BottomSheet open={open} onClose={onClose} header={header} labelledBy={TITLE_ID} snapPoints="full">
      <div className="flex flex-col gap-4">
        {blocked && (
          <div
            role="alert"
            data-testid="meal-edit-blocked"
            className="flex flex-col gap-2 rounded-md border border-bad/50 bg-bad/10 px-4 py-3 text-body-s text-bad"
          >
            <div className="flex items-start gap-2 font-bold">
              <Icon.AlertShield size={20} className="mt-px shrink-0" />
              <span>{t('meny.edit.allergenBlocked')}</span>
            </div>
            <label className="flex min-h-11 items-center gap-3 font-extrabold" data-testid="meal-edit-override">
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
                className="h-5 w-5 shrink-0 accent-bad"
              />
              {t('meny.edit.allergenOverride')}
            </label>
          </div>
        )}

        <div className="flex flex-col">
          <SectionLabel as="label" htmlFor="meal-edit-title">
            {t('meny.edit.fieldTitle')}
          </SectionLabel>
          <input
            id="meal-edit-title"
            className="field text-heading-s font-bold"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="meal-edit-title"
          />
        </div>

        <div className="flex flex-col">
          <SectionLabel as="label" htmlFor="meal-edit-date">
            {t('meny.edit.fieldDate')}
          </SectionLabel>
          <input
            id="meal-edit-date"
            type="date"
            lang="nb"
            className="field"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="meal-edit-date"
          />
        </div>

        <div className="flex flex-col">
          <SectionLabel as="span" id="meal-edit-ingredients-label">
            {t('meny.edit.fieldIngredients')}
          </SectionLabel>
          <div
            role="group"
            aria-labelledby="meal-edit-ingredients-label"
            className="flex flex-col gap-2"
            data-testid="meal-edit-ingredients"
          >
            {ingredients.map((ing, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  className="field"
                  value={ing}
                  aria-label={t('meny.edit.ingredientN', { n: i + 1 })}
                  onChange={(e) => setIngredients(ingredients.map((v, idx) => (idx === i ? e.target.value : v)))}
                  data-testid={`meal-edit-ingredient-${i}`}
                />
                <button
                  type="button"
                  onClick={() => setIngredients(ingredients.filter((_, idx) => idx !== i))}
                  aria-label={t('meny.edit.removeIngredient')}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-inset"
                  data-testid={`meal-edit-ingredient-remove-${i}`}
                >
                  <Icon.Trash size={18} />
                </button>
              </div>
            ))}
          </div>
          <Button
            variant="secondary"
            size={44}
            onClick={() => setIngredients([...ingredients, ''])}
            data-testid="meal-edit-ingredient-add"
            className="mt-2 self-start"
          >
            <Icon.Plus size={16} />
            {t('meny.edit.addIngredient')}
          </Button>
        </div>

        <div className="flex flex-col">
          <SectionLabel as="label" htmlFor="meal-edit-notes">
            {t('meny.edit.fieldNotes')}
          </SectionLabel>
          <input
            id="meal-edit-notes"
            className="field"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="meal-edit-notes"
          />
        </div>

        {/* Slett i to trinn, alene nederst som på Hendelse-arket. */}
        {confirmDelete ? (
          <Button
            variant="danger"
            size={52}
            onClick={onDelete}
            disabled={busy}
            data-testid="meal-edit-delete-confirm"
            className="w-full"
          >
            {t('meny.edit.deleteConfirm')}
          </Button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={busy}
            data-testid="meal-edit-delete"
            className="h-13 w-full rounded-pill border border-bad text-sm font-extrabold text-bad disabled:opacity-40"
          >
            {t('meny.edit.delete')}
          </button>
        )}
      </div>
    </BottomSheet>
  );
}
